import React, { useMemo, useState } from "react";
import { PageHeader, Field, Result, FormulaBox, Note, NumberInput } from "@/components/CalcUI";
import { useT } from "@/i18n/LanguageProvider";
import { cn, fmt } from "@/lib/utils";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function computeEDD(lmp) {
  const d = new Date(lmp.getFullYear(), lmp.getMonth(), lmp.getDate());
  d.setDate(d.getDate() + 7);
  d.setMonth(d.getMonth() - 3);
  d.setFullYear(d.getFullYear() + 1);
  return d;
}

function formatDate(d, lang) {
  return d.toLocaleDateString(lang === "id" ? "id-ID" : "en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function trimesterOf(weeks) {
  if (weeks < 14) return 1;
  if (weeks < 28) return 2;
  return 3;
}

export default function ObGyn() {
  const t = useT();
  const todayDefault = startOfDay(new Date());

  const [lmpStr, setLmpStr] = useState("");
  const [refDateStr, setRefDateStr] = useState(todayDefault.toISOString().slice(0, 10));

  // Johnson-Toshack
  const [tfu, setTfu] = useState("");
  const [station, setStation] = useState("high"); // 'high' → m=12, 'low' → m=11

  const lmp = useMemo(() => parseISO(lmpStr), [lmpStr]);
  const refDate = useMemo(
    () => (refDateStr ? startOfDay(parseISO(refDateStr) || todayDefault) : todayDefault),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refDateStr]
  );

  const result = useMemo(() => {
    if (!lmp) return null;
    if (lmp > refDate) return { error: "future" };
    const diffDays = Math.floor((refDate - lmp) / MS_PER_DAY);
    const totalWeeks = Math.floor(diffDays / 7);
    if (totalWeeks > 45) return { error: "too_far" };

    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;
    const edd = computeEDD(lmp);
    const eddDiff = Math.floor((edd - refDate) / MS_PER_DAY);
    const trimester = trimesterOf(weeks);

    return { weeks, days, diffDays, edd, eddDiff, trimester };
  }, [lmp, refDate]);

  const lang = (typeof document !== "undefined" && document.documentElement.lang) || "en";

  const eddText = result?.edd ? formatDate(result.edd, lang) : "—";
  const trimesterLabel = result?.trimester ? t(`obgyn.trimester.${result.trimester}`) : "—";

  // Johnson-Toshack
  const m = station === "high" ? 12 : 11;
  const tfuNum = parseFloat(tfu);
  const efw = useMemo(() => {
    if (!tfuNum || tfuNum <= 0) return null;
    const w = (tfuNum - m) * 155;
    return w > 0 ? w : null;
  }, [tfuNum, m]);

  const inputClass = cn(
    "w-full bg-white border border-stone-200 rounded-sm",
    "px-3 py-3 text-base font-mono tabular-nums min-h-[44px]",
    "transition-colors duration-100",
    "hover:border-stone-300 focus:border-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-700/30"
  );

  return (
    <div data-testid="page-obgyn">
      <PageHeader
        title={t("obgyn.title")}
        subtitle={t("obgyn.subtitle")}
        formula={t("obgyn.formula")}
      />

      <div className="px-6 md:px-10 py-8 max-w-5xl space-y-12">
        {/* SECTION 1 — Gestational age */}
        <section className="space-y-6 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="section-ga">
          <div className="space-y-6">
            <Field label={t("obgyn.lmp")} hint={t("obgyn.lmp.hint")} testid="lmp">
              <input
                type="date"
                value={lmpStr}
                max={refDateStr}
                onChange={(e) => setLmpStr(e.target.value)}
                data-testid="lmp-input"
                className={inputClass}
              />
            </Field>

            <Field label={t("obgyn.today")} hint={t("obgyn.today.hint")} testid="ref-date">
              <input
                type="date"
                value={refDateStr}
                onChange={(e) => setRefDateStr(e.target.value)}
                data-testid="ref-date-input"
                className={inputClass}
              />
            </Field>

            {result?.error === "future" && (
              <Note kind="danger">{t("obgyn.future_lmp")}</Note>
            )}
            {result?.error === "too_far" && (
              <Note kind="warning">{t("obgyn.too_far")}</Note>
            )}

            <Note>{t("obgyn.note")}</Note>
          </div>

          <div className="space-y-4">
            <Result
              label={t("obgyn.result.ga")}
              value={
                result && !result.error
                  ? `${result.weeks}\u00A0${t("obgyn.weeks")} ${result.days}\u00A0${t("obgyn.days")}`
                  : "—"
              }
              big
              testid="result-ga"
            />
            <Result label={t("obgyn.result.edd")} value={eddText} testid="result-edd" />
            <Result
              label={t("obgyn.result.trimester")}
              value={result && !result.error ? trimesterLabel : "—"}
              testid="result-trimester"
            />
            <Result
              label={t("obgyn.result.days_remaining")}
              value={
                result && !result.error
                  ? result.eddDiff >= 0
                    ? `${result.eddDiff}`
                    : `+${Math.abs(result.eddDiff)} (${t("obgyn.result.past_due")})`
                  : "—"
              }
              unit={result && !result.error ? t("obgyn.result.days_remaining.unit") : undefined}
              testid="result-days-remaining"
              accent={result && !result.error && result.eddDiff < 0 ? "danger" : "default"}
            />
            <FormulaBox testid="obgyn-breakdown">
              {result && !result.error
                ? `LMP   = ${formatDate(lmp, lang)}\nRef.  = ${formatDate(refDate, lang)}\nΔ     = ${result.diffDays} d  →  ${result.weeks} ${t("obgyn.weeks")} ${result.days} ${t("obgyn.days")}\nEDD   = LMP + 7d − 3m + 1y = ${eddText}`
                : t("obgyn.empty")}
            </FormulaBox>
          </div>
        </section>

        {/* SECTION 2 — Johnson-Toshack EFW */}
        <section className="space-y-6 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="section-johnson">
          <div className="space-y-6">
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-sky-700 mb-1">
                {t("common.section")}
              </div>
              <h2 className="font-display text-2xl tracking-tight font-medium">
                {t("johnson.title")}
              </h2>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">{t("johnson.subtitle")}</p>
            </div>

            <Field label={t("johnson.tfu")} unit="cm" testid="tfu">
              <NumberInput value={tfu} onChange={setTfu} placeholder="e.g. 32" min={0} testid="tfu-input" />
            </Field>

            <Field label={t("johnson.station")} testid="station">
              <div
                className="inline-flex border border-stone-200 rounded-sm overflow-hidden bg-white w-full"
                role="group"
                data-testid="station-group"
              >
                {[
                  { v: "high", labelKey: "johnson.station.high" },
                  { v: "low", labelKey: "johnson.station.low" },
                ].map((opt) => {
                  const active = station === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`station-${opt.v}`}
                      onClick={() => setStation(opt.v)}
                      className={cn(
                        "flex-1 px-3 text-sm transition-colors duration-100 min-h-[44px]",
                        active ? "bg-sky-700 text-white" : "bg-white text-stone-700 hover:bg-stone-50"
                      )}
                      aria-pressed={active}
                    >
                      {t(opt.labelKey)}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Note>{t("johnson.note")}</Note>
          </div>

          <div className="space-y-4">
            <Result
              label={t("johnson.result.efw")}
              value={efw !== null ? fmt(efw, 0) : "—"}
              unit="g"
              big
              testid="result-efw"
            />
            <FormulaBox testid="johnson-breakdown">
              {efw !== null
                ? `m = ${m} (${station === "high" ? t("johnson.station.high") : t("johnson.station.low")})\nEFW = (${fmt(tfuNum, 1)} − ${m}) × 155 = ${fmt(efw, 0)} g`
                : t("johnson.empty")}
            </FormulaBox>
          </div>
        </section>
      </div>
    </div>
  );
}
